import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * StructuredData Component
 * Used to add JSON-LD structured data to pages
 * 
 * Props:
 * - data: JavaScript object that will be converted to JSON
 * - type: Type of structured data (optional, used as className)
 */
const StructuredData = ({ data, type = 'structured-data' }) => {
  return (
    <Helmet>
      <script
        type="application/ld+json"
        className={type}
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
    </Helmet>
  );
};

export default StructuredData;

/**
 * Common Schema Builders
 */

// Organization Schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bright Angel Study Abroad',
  description: 'Study Visa Consultant and Admission Agency in Sri Lanka',
  url: 'https://brightangel.lk',
  telephone: '+94778973041',
  email: 'studyvisa@brightangel.lk',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '48/3/1, Galle Road',
    addressLocality: 'Dehiwala',
    addressCountry: 'LK',
  },
  priceRange: '$$',
  areaServed: ['LK', 'SG', 'MY', 'GB', 'CA', 'AU'],
  sameAs: [
    'https://www.facebook.com/brightangelstudyabroad',
    'https://www.linkedin.com/company/bright-angel-study-abroad',
    'https://www.instagram.com/brightangel_studyabroad',
  ],
};

// BreadcrumbList Schema
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

// FAQPage Schema
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Product/Service Schema
export const serviceSchema = (name, description, image, price = 'Call for pricing') => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  image,
  offers: {
    '@type': 'Offer',
    priceCurrency: 'LKR',
    price,
  },
});

// Event Schema (for consultations, webinars, etc.)
export const eventSchema = (name, description, startDate, endDate, location, url) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name,
  description,
  startDate,
  endDate,
  location: {
    '@type': 'Place',
    name: location,
  },
  url,
});

// Article/Blog Schema
export const articleSchema = (headline, description, image, datePublished, dateModified, author = 'Bright Angel Study Abroad') => ({
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline,
  image,
  datePublished,
  dateModified,
  author: {
    '@type': 'Organization',
    name: author,
  },
  description,
});
